// Iter 42.4 — In-app browser detection + global client-error capture.
// Why: Instagram WebView on Android aggressively kills the tab during
// large XHR uploads ("Instagram isn't responding" OS dialog). We can't
// fix it server-side — only detect, warn the user, and ship analytics
// so the founder can quantify the impact.

export function getClientEnv() {
  if (typeof navigator === "undefined") return {
    browser: "unknown", os: "unknown", device_type: "unknown",
    in_app_browser: null, ua: "",
  };
  const ua_raw = navigator.userAgent || "";
  const ua = ua_raw.toLowerCase();

  let device_type = "desktop";
  if (/ipad|tablet/.test(ua)) device_type = "tablet";
  else if (/mobi|iphone|android.*mobile/.test(ua)) device_type = "mobile";

  let os = "other";
  if (/iphone|ipad|ipod/.test(ua)) os = "ios";
  else if (/android/.test(ua)) os = "android";
  else if (/windows/.test(ua)) os = "windows";
  else if (/mac os x/.test(ua)) os = "macos";
  else if (/linux/.test(ua)) os = "linux";

  // In-app browser detection — separated from main browser tag so the
  // dashboard can break out uploads by host app.
  // Instagram UA examples (Android): "...Instagram 295.0.0.32.119..."
  // Facebook  UA examples (Android): "...FBAN/FB4A;FBAV/..."
  let in_app_browser = null;
  if (/instagram/i.test(ua_raw))                       in_app_browser = "instagram";
  else if (/(fban|fbav|\bfb_iab\b)/i.test(ua_raw))     in_app_browser = "facebook";
  else if (/twitter/i.test(ua_raw) && /android/.test(ua)) in_app_browser = "twitter";
  else if (/line\//i.test(ua_raw))                     in_app_browser = "line";
  else if (/snapchat/i.test(ua_raw))                   in_app_browser = "snapchat";
  else if (/tiktok/i.test(ua_raw))                     in_app_browser = "tiktok";
  else if (/wv\)|; wv;/.test(ua))                      in_app_browser = "android_webview";

  let browser = "other";
  if (in_app_browser)                                  browser = `inapp_${in_app_browser}`;
  else if (/edg\//.test(ua))                           browser = "edge";
  else if (/chrome\//.test(ua) && !/edg\//.test(ua))   browser = "chrome";
  else if (/safari\//.test(ua) && !/chrome\//.test(ua)) browser = "safari";
  else if (/firefox\//.test(ua))                       browser = "firefox";

  return { browser, os, device_type, in_app_browser, ua: ua_raw.slice(0, 240) };
}

export function isInstagramWebView() {
  return getClientEnv().in_app_browser === "instagram";
}

// In-app webview banner only when it's KNOWN to cause upload pain. Today
// that's Instagram on Android (largest impact) + Facebook FBAN.
export function isProblematicInAppBrowser() {
  const env = getClientEnv();
  return env.in_app_browser === "instagram" || env.in_app_browser === "facebook";
}

// ---------- Global error / crash capture ----------
// Mount once at app boot. Fire-and-forget — never blocks the page.
let _wired = false;

// Iter 59.1 — import the build id directly so EVERY client-error and
// client-event posted from here carries it as both a header (when
// fetch is used) and a body field (sendBeacon can't set headers).
// We import lazily to avoid a circular dependency on api.js at module
// init time.
function _frontendBuildId() {
  try {
    // eslint-disable-next-line global-require
    return require("./api").FRONTEND_BUILD_ID || "dev-unknown";
  } catch {
    return "dev-unknown";
  }
}

export function setupGlobalErrorCapture({ getSessionId, apiBase }) {
  if (_wired || typeof window === "undefined") return;
  _wired = true;

  const post = (kind, payload) => {
    try {
      const sessionId = (typeof getSessionId === "function") ? getSessionId() : null;
      const env = getClientEnv();
      const body = JSON.stringify({
        session_id: sessionId,
        kind, // 'js_error' | 'unhandled_rejection' | 'page_unload' | 'page_hide'
        ...payload,
        browser:        env.browser,
        in_app_browser: env.in_app_browser,
        os:             env.os,
        device_type:    env.device_type,
        ua:             env.ua,
        url:            (typeof location !== "undefined") ? location.href.slice(0, 240) : null,
        ts:             new Date().toISOString(),
        // Iter 59.1 — frontend build id stamped on every client-error
        // payload so the founder can correlate client crashes with
        // the exact bundle that produced them. Mirrored on the
        // request header below for the fetch path.
        frontend_build_id: _frontendBuildId(),
      });
      // navigator.sendBeacon survives page unload — fire and forget.
      // (sendBeacon does NOT support custom headers — the build id
      // travels in the body for this transport.)
      const url = `${apiBase}/web/client-error`;
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
      // Fallback for browsers without sendBeacon
      fetch(url, { method: "POST", body, headers: {
                     "Content-Type": "application/json",
                     "X-Frontend-Build-Id": _frontendBuildId(),
                   }, keepalive: true }).catch(() => {});
    } catch { /* swallow — analytics must not break the page */ }
  };

  window.addEventListener("error", (e) => {
    post("js_error", {
      message: (e?.message || "").slice(0, 400),
      source:  (e?.filename || "").slice(0, 240),
      lineno:  e?.lineno || null,
      colno:   e?.colno || null,
      stack:   (e?.error?.stack || "").slice(0, 2000),
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    post("unhandled_rejection", {
      message: ((reason && (reason.message || String(reason))) || "").slice(0, 400),
      stack:   (reason?.stack || "").slice(0, 2000),
    });
  });

  // pagehide is the most reliable cross-browser "tab going away" signal.
  // Instagram WebView fires this when the OS kills the tab — exactly the
  // case we want to count.
  window.addEventListener("pagehide", (e) => {
    post("page_hide", { persisted: !!e.persisted });
  });
  // beforeunload as a belt-and-suspenders for desktop browsers
  window.addEventListener("beforeunload", () => {
    post("page_unload", {});
  });
}

// One-shot lightweight event poster (for upload_started / upload_completed /
// review_started / review_completed). Same /web/client-error sink — backend
// classifies by `kind`.
export function postClientEvent(apiBase, sessionId, kind, payload = {}) {
  try {
    const env = getClientEnv();
    // Iter XX.92 — Spread payload FIRST so callers can't accidentally
    // shadow the outer `kind` (bug A from iteration_104: a caller
    // passing `{kind: 'typed'}` inside payload silently overwrote
    // the top-level event name and analytics went dark). Now the
    // authoritative event `kind`, session_id and env fields always
    // win — payload can carry any auxiliary keys it wants.
    const body = JSON.stringify({
      ...payload,
      session_id: sessionId, kind,
      browser: env.browser, in_app_browser: env.in_app_browser,
      os: env.os, device_type: env.device_type,
      url: (typeof location !== "undefined") ? location.href.slice(0, 240) : null,
      ts: new Date().toISOString(),
      // Iter 59.1 — frontend build id on every client event payload.
      frontend_build_id: _frontendBuildId(),
    });
    const url = `${apiBase}/web/client-error`;
    fetch(url, { method: "POST", body, headers: {
                   "Content-Type": "application/json",
                   "X-Frontend-Build-Id": _frontendBuildId(),
                 }, keepalive: true }).catch(() => {});
  } catch { /* swallow */ }
}

// Iter 25.9k — Confirmed-write event poster. Returns a Promise resolving
// to { ok: true, status } on backend 2xx, { ok: false, status } on
// failure / timeout. Caller MUST await this BEFORE rendering any UI
// that references the supplied client_ref / video_analysis_id —
// otherwise the user can see a `cref_*` reference that the trace
// endpoint cannot find any rows for ("zero rows" trace bug).
// Defaults to a 5s timeout so a hung audit cannot indefinitely block
// the upload UI.
export async function postClientEventAwait(apiBase, sessionId, kind, payload = {}, timeoutMs = 5000) {
  try {
    const env = getClientEnv();
    const body = JSON.stringify({
      session_id: sessionId, kind, ...payload,
      browser: env.browser, in_app_browser: env.in_app_browser,
      os: env.os, device_type: env.device_type,
      url: (typeof location !== "undefined") ? location.href.slice(0, 240) : null,
      ts: new Date().toISOString(),
      // Iter 59.1 — frontend build id stamped on confirmed-write
      // events too (these are the audit cref writes).
      frontend_build_id: _frontendBuildId(),
    });
    const url = `${apiBase}/web/client-error`;
    const controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => { try { controller.abort(); } catch { /* noop */ } }, timeoutMs)
      : null;
    let resp;
    try {
      resp = await fetch(url, {
        method: "POST",
        body,
        headers: {
          "Content-Type": "application/json",
          "X-Frontend-Build-Id": _frontendBuildId(),
        },
        signal: controller ? controller.signal : undefined,
      });
    } finally {
      if (timer) clearTimeout(timer);
    }
    if (resp && resp.ok) return { ok: true, status: resp.status };
    return { ok: false, status: resp ? resp.status : 0 };
  } catch (e) {
    return { ok: false, status: 0, error: (e && e.message) ? String(e.message).slice(0, 200) : "fetch_failed" };
  }
}
